import { jsx } from 'react/jsx-runtime';
import React, { useContext, useMemo } from 'react';
import { safeRest, defaultBaseline, defaultFonts, withNested, withSpacing, clsx } from '@wds/utils';
import { createMemoStyles } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var GridContext = /*#__PURE__*/React.createContext({
  noGutters: false
});

var useStyles = createMemoStyles(function (theme) {
  var _a;
  var spacing = theme.spacing,
    breakpoints = theme.breakpoints;
  var grid = {
    gutter: {
      xs: spacing[4],
      sm: spacing[5],
      md: spacing[5],
      lg: spacing[5],
      xl: spacing[5]
    },
    columns: 12
  };
  var width = function width(sizeValue) {
    return sizeValue ? "".concat(100 * (sizeValue / grid.columns), "%") : '0%';
  };
  return {
    wrenchGridItem: (_a = {
      boxSizing: 'border-box',
      flexGrow: 1,
      flexBasis: 0,
      maxWidth: '100%'
    }, _a["@media (min-width: ".concat(breakpoints.xs, "px)")] = {
      flex: function (_a) {
        var xs = _a.xs;
        return xs && "0 0 ".concat(width(xs));
      },
      maxWidth: function (_a) {
        var xs = _a.xs;
        return xs && width(xs);
      },
      order: function (_a) {
        var xsOrder = _a.xsOrder;
        return xsOrder;
      },
      paddingLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xs / 2;
      },
      paddingRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xs / 2;
      }
    }, _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
      flex: function (_a) {
        var sm = _a.sm;
        return sm && "0 0 ".concat(width(sm));
      },
      maxWidth: function (_a) {
        var sm = _a.sm;
        return sm && width(sm);
      },
      order: function (_a) {
        var smOrder = _a.smOrder;
        return smOrder;
      },
      paddingLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.sm / 2;
      },
      paddingRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.sm / 2;
      }
    }, _a["@media (min-width: ".concat(breakpoints.md, "px)")] = {
      flex: function (_a) {
        var md = _a.md;
        return md && "0 0 ".concat(width(md));
      },
      maxWidth: function (_a) {
        var md = _a.md;
        return md && width(md);
      },
      order: function (_a) {
        var mdOrder = _a.mdOrder;
        return mdOrder;
      },
      paddingLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.md / 2;
      },
      paddingRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.md / 2;
      }
    }, _a["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
      flex: function (_a) {
        var lg = _a.lg;
        return lg && "0 0 ".concat(width(lg));
      },
      maxWidth: function (_a) {
        var lg = _a.lg;
        return lg && width(lg);
      },
      order: function (_a) {
        var lgOrder = _a.lgOrder;
        return lgOrder;
      },
      paddingLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.lg / 2;
      },
      paddingRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.lg / 2;
      }
    }, _a["@media (min-width: ".concat(breakpoints.xl, "px)")] = {
      flex: function (_a) {
        var xl = _a.xl;
        return xl && "0 0 ".concat(width(xl));
      },
      maxWidth: function (_a) {
        var xl = _a.xl;
        return xl && width(xl);
      },
      order: function (_a) {
        var xlOrder = _a.xlOrder;
        return xlOrder;
      },
      paddingLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xl / 2;
      },
      paddingRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xl / 2;
      }
    }, _a)
  };
});

var GridItem = function GridItem(_a) {
  var children = _a.children,
    xs = _a.xs,
    sm = _a.sm,
    md = _a.md,
    lg = _a.lg,
    xl = _a.xl,
    xsOrder = _a.xsOrder,
    smOrder = _a.smOrder,
    mdOrder = _a.mdOrder,
    lgOrder = _a.lgOrder,
    xlOrder = _a.xlOrder,
    noGutters = _a.noGutters,
    rest = __rest(_a, ["children", "xs", "sm", "md", "lg", "xl", "xsOrder", "smOrder", "mdOrder", "lgOrder", "xlOrder", "noGutters"]);
  var noGuttersFromContext = useContext(GridContext).noGutters;
  var wrenchGridItem = useStyles({
    xs: xs,
    sm: sm,
    md: md,
    lg: lg,
    xl: xl,
    noGutters: noGutters !== null && noGutters !== void 0 ? noGutters : noGuttersFromContext,
    xsOrder: xsOrder,
    smOrder: smOrder,
    mdOrder: mdOrder,
    lgOrder: lgOrder,
    xlOrder: xlOrder
  }).wrenchGridItem;
  return jsx("div", __assign({
    className: wrenchGridItem
  }, safeRest(rest), {
    children: children
  }));
};

var useStylesGrid = createMemoStyles(function (theme) {
  var _a, _b;
  var breakpoints = theme.breakpoints,
    spacing = theme.spacing;
  var grid = {
    bleed: {
      xs: spacing[4],
      sm: spacing[6],
      md: spacing[6],
      lg: spacing[6],
      xl: spacing[6]
    },
    gutter: {
      xs: spacing[4],
      sm: spacing[5],
      md: spacing[5],
      lg: spacing[5],
      xl: spacing[5]
    },
    maxWidth: __assign(__assign({}, breakpoints), {
      xl: 1400
    })};
  var GridStyles = {
    wrenchGridContainer: __assign(__assign(__assign({}, defaultBaseline), defaultFonts(theme)), (_a = {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      marginRight: 'auto',
      marginLeft: 'auto'
    }, _a["@media (min-width: ".concat(breakpoints.xs, "px)")] = {
      paddingLeft: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.xs;
      },
      paddingRight: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.xs;
      }
    }, _a["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
      maxWidth: function (_a) {
        var fluid = _a.fluid;
        return !fluid && grid.maxWidth.sm;
      },
      paddingLeft: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.sm;
      },
      paddingRight: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.sm;
      }
    }, _a["@media (min-width: ".concat(breakpoints.md, "px)")] = {
      maxWidth: function (_a) {
        var fluid = _a.fluid;
        return !fluid && grid.maxWidth.md;
      },
      paddingLeft: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.md;
      },
      paddingRight: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.md;
      }
    }, _a["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
      maxWidth: function (_a) {
        var fluid = _a.fluid;
        return !fluid && grid.maxWidth.lg;
      },
      paddingLeft: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.lg;
      },
      paddingRight: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.lg;
      }
    }, _a["@media (min-width: ".concat(breakpoints.xl, "px)")] = {
      maxWidth: function (_a) {
        var fluid = _a.fluid;
        return !fluid && grid.maxWidth.xl;
      },
      paddingLeft: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.xl;
      },
      paddingRight: function (_a) {
        var noBleeds = _a.noBleeds;
        return !noBleeds && grid.bleed.xl;
      }
    }, _a)),
    wrenchGrid: (_b = {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      rowGap: function (_a) {
        var verticalGap = _a.verticalGap;
        return verticalGap ? "".concat(theme.spacing[verticalGap], "px") : undefined;
      }
    }, _b["@media (min-width: ".concat(breakpoints.xs, "px)")] = {
      marginLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xs / -2;
      },
      marginRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xs / -2;
      }
    }, _b["@media (min-width: ".concat(breakpoints.sm, "px)")] = {
      marginLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.sm / -2;
      },
      marginRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.sm / -2;
      }
    }, _b["@media (min-width: ".concat(breakpoints.md, "px)")] = {
      marginLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.md / -2;
      },
      marginRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.md / -2;
      }
    }, _b["@media (min-width: ".concat(breakpoints.lg, "px)")] = {
      marginLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.lg / -2;
      },
      marginRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.lg / -2;
      }
    }, _b["@media (min-width: ".concat(breakpoints.xl, "px)")] = {
      marginLeft: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xl / -2;
      },
      marginRight: function (_a) {
        var noGutters = _a.noGutters;
        return noGutters ? 0 : grid.gutter.xl / -2;
      }
    }, _b)
  };
  return GridStyles;
});

var Grid = function Grid(_a) {
  var children = _a.children,
    _b = _a.noGutters,
    noGutters = _b === void 0 ? false : _b,
    _c = _a.noBleeds,
    noBleeds = _c === void 0 ? false : _c,
    _d = _a.fluid,
    fluid = _d === void 0 ? false : _d,
    spacingClass = _a.spacingClass,
    verticalGap = _a.verticalGap,
    rest = __rest(_a, ["children", "noGutters", "noBleeds", "fluid", "spacingClass", "verticalGap"]);
  var _e = useStylesGrid({
      noGutters: noGutters,
      noBleeds: noBleeds,
      fluid: fluid,
      verticalGap: verticalGap
    }),
    wrenchGrid = _e.wrenchGrid,
    wrenchGridContainer = _e.wrenchGridContainer;
  var wrenchGridWrapper = clsx(wrenchGridContainer, spacingClass);
  var contextValue = useMemo(function () {
    return {
      noGutters: noGutters
    };
  }, [noGutters]);
  return jsx(GridContext.Provider, {
    value: contextValue,
    children: jsx("div", __assign({
      className: wrenchGridWrapper
    }, safeRest(rest), {
      children: jsx("div", {
        className: wrenchGrid,
        children: children
      })
    }))
  });
};
var GridWithSpacing = withNested(withSpacing(Grid), {
  Item: GridItem
});

export { GridWithSpacing as Grid };
